import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { State } from '®app/ngrx/state';
import { PlayerResourceUtils } from '®app/shared/player-resource';
import {
  BibliographicResourceType,
  OnlineResourceUrl,
} from '®shared/bibliographic-resource';
import { ResourceImageUtils } from '®shared/container-structure';
import {
  FetchPageContentAction,
  LoadablePageResource,
  selectPageContentForId,
} from '®shared/list-item';
import {
  Permissions,
  UserPermissions,
  hasPermissionForListId,
} from '®shared/permissions';
import { ListItem } from '®shared/reading-list/models';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rl-single-item-view',
  templateUrl: './single-item-view.component.html',
})
export class SingleItemViewComponent implements OnChanges, OnDestroy {
  @Input() item: ListItem;
  @Input() isDraft: boolean;
  @Input() currentUserPermissions: UserPermissions;
  @Input() listId: string;
  @Input() listTitle: string;
  @Input() listUrl: string;
  @Input() resourceEditUrl: string;

  isNonPageResource: boolean;
  isPageResource: boolean;
  onlineResourceUrl?: string;
  pageContent?: LoadablePageResource;
  private pageContentSubscription: Subscription;
  resourceId?: string;
  resourceImage: string;
  hasListEditPermission = false;

  constructor(private cd: ChangeDetectorRef, private store: Store<State>) {}

  ngOnDestroy() {
    if (this.pageContentSubscription) {
      this.pageContentSubscription.unsubscribe();
    }
  }

  ngOnChanges(_changes?: SimpleChanges) {
    if (this.currentUserPermissions) {
      this.hasListEditPermission = hasPermissionForListId(
        Permissions.EDIT_LIST,
        this.listId,
        this.currentUserPermissions,
      );
    }

    this.resourceId = this.item && this.item.resource && this.item.resource.id;

    // Wrappers for resource types
    this.isNonPageResource =
      this.resourceId &&
      this.item.resource.resource_type !== BibliographicResourceType.Page;
    this.isPageResource =
      this.resourceId &&
      this.item.resource.resource_type === BibliographicResourceType.Page;

    this.onlineResourceUrl =
      this.item.defaultOnlineResource &&
      (this.item.defaultOnlineResource as OnlineResourceUrl).original_url;

    // add a placeholder image for resource
    if (!this.resourceImage && this.resourceId) {
      this.resourceImage = ResourceImageUtils.buildPlaceholderImageURL(
        this.item.resource,
        400,
      );
    }

    if (PlayerResourceUtils.isPlayerUrl(this.onlineResourceUrl)) {
      this.resourceImage = PlayerResourceUtils.getThumbnailUrl(
        this.onlineResourceUrl,
      );
    }

    // Observables for page content
    if (this.isPageResource) {
      const { id: pageId } = this.item.resource;
      this.pageContentSubscription = this.store
        .select(selectPageContentForId(this.item.resource.id))
        .pipe(
          tap(pageContent => {
            if (!pageContent) {
              this.store.dispatch(new FetchPageContentAction({ pageId }));
            }
          }),
          filter(loadablePageContent => (loadablePageContent ? true : false)),
        )
        .subscribe(pageContent => {
          this.pageContent = pageContent;
          this.cd.markForCheck();
        });
    }
  }
}
